import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useDarkMode } from "../contexts/DarkModeContext";
import Navbar from "./Navbar";
import Footer from "./Footer";

// Floating Dark Mode Toggle Component
function FloatingDarkModeToggle() {
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={toggleDarkMode}
      className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200"
      aria-label="Toggle dark mode"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3, delay: 1 }}
    >
      {isDarkMode ? (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </motion.button>
  );
}

const SponsorContact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    companyName: "",
    companyWebsite: "",
    pointOfContact: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = "error") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleScrollToSection = (sectionId) => {
    navigate(`/#${sectionId}`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare data for Supabase
      const sponsorData = {
        email: formData.email.toLowerCase(),
        company_name: formData.companyName,
        company_website: formData.companyWebsite,
        point_of_contact: formData.pointOfContact,
        message: formData.message,
        recipient_email: "test@aifund.ai",
        status: "pending",
        submitted_at: new Date().toISOString(),
      };

      // Submit to Supabase
      const { error } = await supabase
        .from("sponsor_inquiries")
        .insert([sponsorData]);

      if (error) {
        console.error("Submission error:", error);
      }

      // Send email via Edge Function
      try {
        const { error: emailError } = await supabase.functions.invoke(
          "send-sponsor-email",
          {
            body: {
              email: formData.email,
              company_name: formData.companyName,
              company_website: formData.companyWebsite,
              point_of_contact: formData.pointOfContact,
              message: formData.message,
            },
          }
        );

        if (emailError) {
          console.error("Email sending error:", emailError);
        }
      } catch (emailError) {
        console.error("Email function error:", emailError);
      }

      // Success
      setIsSubmitted(true);
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
        <Navbar scrollToSection={handleScrollToSection} activeSection="" />
        <div className="flex items-center justify-center pt-16 pb-8 min-h-screen">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          >
            <div className="bg-green-100 dark:bg-green-900/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Thank You!
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              We've received your sponsorship inquiry and will get back to you
              within 2-3 business days.
            </p>
            <motion.a
              href="/"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-aifund text-white px-8 py-3 rounded-full font-semibold hover:bg-aifund-dark transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Back to Home
            </motion.a>
          </motion.div>
        </div>
        
        {/* Footer */}
        <Footer />
        
        {/* Floating Dark Mode Toggle */}
        <FloatingDarkModeToggle />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Navbar scrollToSection={handleScrollToSection} activeSection="" />
      <div className="pt-16 pb-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Become a Sponsor
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Join our community of forward-thinking companies and help shape
              the future of AI innovation.
            </p>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  placeholder="your.email@company.com"
                />
              </div>

              {/* Company Name */}
              <div>
                <label
                  htmlFor="companyName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Company Name *
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  placeholder="Your Company Name"
                />
              </div>

              {/* Company Website */}
              <div>
                <label
                  htmlFor="companyWebsite"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Company Website
                </label>
                <input
                  type="text"
                  id="companyWebsite"
                  name="companyWebsite"
                  value={formData.companyWebsite}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  placeholder="https://yourcompany.com"
                />
              </div>

              {/* Point of Contact */}
              <div>
                <label
                  htmlFor="pointOfContact"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Point of Contact *
                </label>
                <input
                  type="text"
                  id="pointOfContact"
                  name="pointOfContact"
                  value={formData.pointOfContact}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  placeholder="Full Name"
                />
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  placeholder="Tell us about your interest in sponsoring the Buildathon..."
                />
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                  className={`px-8 py-3 rounded-full font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl ${
                    isSubmitting
                      ? "bg-gray-400 text-gray-500 cursor-not-allowed"
                      : "bg-aifund text-white hover:bg-aifund-dark"
                  }`}
                >
                  {isSubmitting ? "Sending..." : "Send Inquiry"}
                </motion.button>
              </div>
            </form>
          </motion.div>

          {/* Notification */}
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
                notification.type === "success"
                  ? "bg-green-100 text-green-800 border-l-4 border-green-500"
                  : "bg-red-100 text-red-800 border-l-4 border-red-500"
              }`}
            >
              {notification.message}
            </motion.div>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
      
      {/* Floating Dark Mode Toggle */}
      <FloatingDarkModeToggle />
    </div>
  );
};

export default SponsorContact;
